import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Phone, Shield, Bell, Save, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../services/auth';

type NotificationPrefs = {
  email: boolean;
  sms: boolean;
  push: boolean;
};

export const Settings: React.FC = () => {
  const { user, refreshUser } = useAuth();

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });

  const [notifications, setNotifications] = useState<NotificationPrefs>({
    email: true,
    sms: false,
    push: true,
  });

  const [passwords, setPasswords] = useState({
    current: '',
    next: '',
    confirm: '',
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        email: user.email || '',
      });
      setNotifications(
        user.notificationPrefs || {
          email: true,
          sms: false,
          push: true,
        }
      );
    }
  }, [user]);

  const profileChanged = useMemo(() => {
    if (!user) return false;
    return (
      profile.firstName !== (user.firstName || '') ||
      profile.lastName !== (user.lastName || '') ||
      profile.phoneNumber !== (user.phoneNumber || '')
    );
  }, [profile, user]);

  const toggleNotification = (key: keyof NotificationPrefs) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProfileSave = async () => {
    try {
      setSavingProfile(true);
      const result = await AuthService.updateProfile({
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        phoneNumber: profile.phoneNumber.trim(),
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to update profile');
      }

      await refreshUser();
      toast.success('Profile updated');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleNotificationSave = async () => {
    try {
      setSavingNotifications(true);
      const result = await AuthService.updateNotificationPrefs(notifications);
      if (!result.success) {
        throw new Error(result.error || 'Failed to save preferences');
      }
      await refreshUser();
      toast.success('Notification preferences saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveAll = async () => {
    await handleProfileSave();
    await handleNotificationSave();
  };

  const handlePasswordChange = async () => {
    if (!passwords.current || !passwords.next || !passwords.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.next.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setUpdatingPassword(true);
      const result = await AuthService.updatePassword(passwords.current, passwords.next);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update password');
      }
      toast.success('Password updated');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const TogglePill = ({
    label,
    description,
    value,
    onChange,
  }: {
    label: string;
    description: string;
    value: boolean;
    onChange: () => void;
  }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-secondary-900">{label}</p>
        <p className="text-sm text-secondary-500">{description}</p>
      </div>
      <button
        type="button"
        onClick={onChange}
        aria-pressed={value}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value ? 'bg-primary-600' : 'bg-secondary-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Settings</h1>
          <p className="text-secondary-600">
            Manage your account, security, and notification preferences
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Button
            variant="outline"
            leftIcon={<Save className="h-4 w-4" />}
            onClick={handleSaveAll}
          >
            Save All
          </Button>
        </div>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Mail className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <CardTitle>Profile</CardTitle>
              <p className="text-sm text-secondary-500">Basic account details</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleProfileSave}
            loading={savingProfile}
            disabled={!profileChanged}
          >
            Save changes
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First name"
            value={profile.firstName}
            onChange={(e) => setProfile((p) => ({ ...p, firstName: e.target.value }))}
            placeholder="Enter first name"
          />
          <Input
            label="Last name"
            value={profile.lastName}
            onChange={(e) => setProfile((p) => ({ ...p, lastName: e.target.value }))}
            placeholder="Enter last name"
          />
          <Input
            label="Email"
            type="email"
            value={profile.email}
            disabled
            helperText="Email is managed by the admin account"
            leftIcon={<Mail className="h-4 w-4" />}
          />
          <Input
            label="Phone number"
            value={profile.phoneNumber}
            onChange={(e) => setProfile((p) => ({ ...p, phoneNumber: e.target.value }))}
            placeholder="+233 00 000 0000"
            leftIcon={<Phone className="h-4 w-4" />}
          />
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-secondary-50 rounded-lg">
              <Shield className="h-5 w-5 text-secondary-700" />
            </div>
            <div>
              <CardTitle>Security</CardTitle>
              <p className="text-sm text-secondary-500">Update your password</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={handlePasswordChange}
            loading={updatingPassword}
          >
            Update password
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Current password"
            type="password"
            value={passwords.current}
            onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
            placeholder="Enter current password"
          />
          <Input
            label="New password"
            type="password"
            value={passwords.next}
            onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
            placeholder="Enter new password"
            helperText="Minimum 6 characters"
          />
          <Input
            label="Confirm new password"
            type="password"
            value={passwords.confirm}
            onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
            placeholder="Re-enter new password"
          />
        </CardContent>
        <CardContent className="border-t border-secondary-200 bg-secondary-50">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5" />
            <p className="text-sm text-secondary-600">
              For enhanced security, consider enabling 2FA in the next release. Avoid reusing
              passwords across systems.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success-50 rounded-lg">
              <Bell className="h-5 w-5 text-success-600" />
            </div>
            <div>
              <CardTitle>Notifications</CardTitle>
              <p className="text-sm text-secondary-500">Choose how you want to be notified</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="success"
            onClick={handleNotificationSave}
            loading={savingNotifications}
          >
            Save preferences
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <TogglePill
            label="Email alerts"
            description="Get booking updates and admin alerts via email"
            value={notifications.email}
            onChange={() => toggleNotification('email')}
          />
          <TogglePill
            label="SMS notifications"
            description="Receive critical alerts via SMS"
            value={notifications.sms}
            onChange={() => toggleNotification('sms')}
          />
          <TogglePill
            label="Push notifications"
            description="Get real-time updates while using the dashboard"
            value={notifications.push}
            onChange={() => toggleNotification('push')}
          />
        </CardContent>
      </Card>

      {/* Account status */}
      <Card>
        <CardHeader className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <CardTitle>Account</CardTitle>
            <p className="text-sm text-secondary-500">
              Your admin account is active and connected to Firebase
            </p>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-secondary-500">Status</p>
            <p className="text-sm font-medium text-success-700">Active</p>
          </div>
          <div>
            <p className="text-sm text-secondary-500">Role</p>
            <p className="text-sm font-medium text-secondary-900">
              {(user?.role || 'user').replace('_', ' ')}
            </p>
          </div>
          <div>
            <p className="text-sm text-secondary-500">Email</p>
            <p className="text-sm font-medium text-secondary-900">{user?.email}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

